import { Box, Divider, styled, TextField } from "@mui/material";
import { Add } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import {
  CheckboxComponent,
  NumberField,
  SelectFieldComponent,
  TextFieldComponent,
} from "../external/ensolvers-core-frontend-mui/components/Common/FormComponents";
import GenericForm from "../external/ensolvers-core-frontend-mui/components/Common/GenericForm";
import AutoCompleteUIDTO from "../dto/AutoCompleteUIDTO";
import { SpinnerWrapper } from "./SpinnerWrapper";
import { DateFieldComponent } from "./DatePicker";
import { BillingPolicy, SUM_HOURS_WITH_MINIMUM_KEY } from "../types/BillingPolicy";
import { RoleKey } from "../types/RoleKey";
import { WorkingHours } from "../utils/enums/WorkingHours";
import Assignation from "../models/Assignation";
import AssignationTransferBuilder from "../models/AssignationTransferBuilder";
import AssignationTransferForm from "../models/AssignationTransferForm";
import { SimpleAutoComplete } from "./SimpleAutoComplete";
import { Button } from "@mui/material";
import { useStyles } from "./Main/formStyles";
import { Token } from "../theme";

interface Props {
  onClose: () => void;
  onSave: (instance: AssignationTransferForm) => void;
  isLoading: boolean;
  positionNames: AutoCompleteUIDTO[];
  projectsNames: AutoCompleteUIDTO[];
  open: boolean;
  assignation: Assignation | undefined;
  currentProject: AutoCompleteUIDTO;
  handleChangeProject: (project: AutoCompleteUIDTO) => void;
  loadTransferBuilder: (positionExternalId: string, assignationExternalId: string) => Promise<AssignationTransferBuilder>;
}

export default function AssignationTransferDialog(props: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [canChangePositionNature, setCanChangePositionNature] = useState<boolean>(false);
  const [minimumHoursPolicy, setMinimumHoursPolicy] = useState<boolean>(false);
  const [assignationTransfer, setAssignationTransfer] = useState<AssignationTransferForm | undefined>(undefined);
  const [positionCreation, setPositionCreation] = useState<boolean>(false);
  //const [selectedProject, setSelectedProject] = useState<AutoCompleteUIDTO>({label:"",externalId:""})

  const customStyles = useStyles();

  useEffect(() => {
    if (props.assignation === undefined && !props.open) {
      handleResetAndClose();
    }
  }, [props.assignation]);

  const handleOnChangeInputBillingPolicy = (event: any) => {
    const value = event.target.value;
    if (value === SUM_HOURS_WITH_MINIMUM_KEY) {
      setMinimumHoursPolicy(true);
    } else {
      setMinimumHoursPolicy(false);
    }
  };

  const handleOnSave = (instance: AssignationTransferForm) => {
    if (positionCreation && props.assignation) {
      instance.externalId = props.assignation.externalId!;
      instance.oldPositionExternalId = props.assignation.position.externalId!;
      instance.userExternalId = props.assignation.userExternalId;
      instance.projectExternalId = props.currentProject.externalId;
    }
    props.onSave(instance);
  };

  const handleChangePosition = (positionAutoComplete: AutoCompleteUIDTO) => {
    setPositionCreation(false);
    if (!props.assignation?.externalId) return;
    setIsLoading(true);
    props
      .loadTransferBuilder(positionAutoComplete.externalId, props.assignation.externalId)
      .then((builder) => {
        const transfer = builder.assignationTransfer;

        let assignationExplicit: AssignationTransferForm = {
          externalId: transfer.externalId,
          userExternalId: transfer.userExternalId,
          oldPositionExternalId: transfer.oldPositionExternalId,
          shortStartDate: new Date(),
          shortEndDate: transfer.shortEndDate,
          externalRoleName: transfer.position.externalRoleName,
          internalRoleName: transfer.position.internalRoleName,
          externalHours: transfer.position.externalHours,
          internalHours: transfer.position.internalHours,
          shadow: transfer.position.shadow,
          bonus: transfer.position.bonus,
          billingPolicy: transfer.position.billingPolicy,
          positionShortEndDate: transfer.position.shortEndDate,
          positionShortStartDate: transfer.position.shortStartDate,
          roleKey: transfer.position.roleKey,
          onDemand: transfer.position.onDemand,
          monthlyMinimumHours: transfer.position.monthlyMinimumHours,
          positionExternalId: transfer.position.externalId!,
          projectExternalId: props.currentProject.externalId,
        };

        setAssignationTransfer(assignationExplicit);
        setCanChangePositionNature(builder.canChangePositionNature);
        setMinimumHoursPolicy(
          BillingPolicy[builder.assignationTransfer.position.billingPolicy] === BillingPolicy[BillingPolicy.SUM_HOURS_WITH_MINIMUM],
        );
      })
      .catch(() => {
        // QueryCache toasts the ApiError.
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleResetAndClose = () => {
    setAssignationTransfer(undefined);
    setCanChangePositionNature(false);
    setMinimumHoursPolicy(false);
    setPositionCreation(false);
    props.onClose();
  };

  const handleNewPosition = () => {
    setIsLoading(true);
    setPositionCreation(true);
    assignationTransfer && setAssignationTransfer(undefined);

    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  };

  if (!props.assignation) {
    return null;
  }

  return (
    <GenericForm<AssignationTransferForm>
      headerStyles={{ color: Token.textPrimary, marginBottom: -10, marginTop: 10 }}
      buttonStyles={{ marginRight: 20, color: "white", backgroundColor: Token.actionPrimary, marginBottom: 10 }}
      cancelButtonStyles={{
        color: Token.textSecondary,
        border: `1px solid ${Token.borderDefault}`,
        marginBottom: 10,
      }}
      dialogStyles={customStyles.root}
      instance={assignationTransfer}
      dialog={true}
      open={!!props.open}
      title={"Assignation Transfer"}
      onSave={(instance) => {
        handleOnSave(instance);
      }}
      onCancel={() => handleResetAndClose()}
      dialogCustomSubmitMessage={"Save"}
      renderContent={(register, errors, control) => (
        <Box>
          <SpinnerWrapper isLoading={isLoading}>
            <TextField
              label={"User Name"}
              required={false}
              disabled
              defaultValue={props.assignation!.userFullName}
              variant={"filled"}
              size="medium"
              sx={{ paddingBottom: "0.5em" }}
            />

            <SimpleAutoComplete
              label="Project"
              disablePortal={false}
              items={props.projectsNames}
              value={props.currentProject}
              onChange={(data) => {
                props.handleChangeProject(data);
              }}
              sx={{ width: "100%" }}
              size="medium"
              variant={"filled"}
            />

            <SimpleAutoComplete
              label="Position Name"
              disablePortal={false}
              items={props.positionNames}
              value={
                props.positionNames.find((p) => p.externalId === assignationTransfer?.positionExternalId) && !positionCreation
                  ? props.positionNames.find((p) => p.externalId === assignationTransfer?.positionExternalId)!
                  : { label: "", externalId: "" }
              }
              onChange={(data) => {
                handleChangePosition(data);
              }}
              sx={{ width: "100%" }}
              size="medium"
              variant={"filled"}
            />

            <Button
              startIcon={<Add />}
              color="primary"
              onClick={() => {
                handleNewPosition();
              }}
            >
              Add new position
            </Button>

            {(assignationTransfer != undefined || positionCreation) && (
              <>
                <DateFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"shortStartDate"}
                  label="Assignation Start Date"
                  required={true}
                  minDate={assignationTransfer ? assignationTransfer.positionShortStartDate : undefined}
                  clearable={true}
                  toUTC
                />

                <DateFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"shortEndDate"}
                  label="Assignation End Date"
                  required={false}
                  maxDate={assignationTransfer ? assignationTransfer.positionShortEndDate : undefined}
                  clearable={true}
                  toUTC
                />

                <Divider sx={{ backgroundColor: Token.borderDefault, marginLeft: 2, marginTop: 3, marginBottom: 3 }} />

                <NumberField
                  defaultValue={WorkingHours.DEFAULT}
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"internalHours"}
                  label="Internal Hours"
                  required={true}
                  readOnly={!canChangePositionNature && !positionCreation}
                />
                <NumberField
                  defaultValue={WorkingHours.DEFAULT}
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"externalHours"}
                  label="External Hours"
                  required={true}
                  readOnly={!canChangePositionNature && !positionCreation}
                />
                <TextFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"externalRoleName"}
                  label="External Role Name"
                  required={true}
                  readOnly={!canChangePositionNature && !positionCreation}
                />
                <TextFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"internalRoleName"}
                  label="Internal Role Name"
                  required={true}
                  readOnly={!canChangePositionNature && !positionCreation}
                />

                <SelectFieldComponent
                  defaultValue={assignationTransfer ? RoleKey[assignationTransfer.roleKey!] : ""}
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"roleKey"}
                  label="Role Key"
                  required={true}
                  readOnly={!canChangePositionNature && !positionCreation}
                  options={[
                    {
                      value: RoleKey[RoleKey.SOFTWARE_ENGINEER],
                      label: RoleKey.SOFTWARE_ENGINEER.toString(),
                    },
                    {
                      value: RoleKey[RoleKey.TECH_LEAD],
                      label: RoleKey.TECH_LEAD.toString(),
                    },
                    {
                      value: RoleKey[RoleKey.QA_ENGINEER],
                      label: RoleKey.QA_ENGINEER.toString(),
                    },
                    {
                      value: RoleKey[RoleKey.PROJECT_MANAGER],
                      label: RoleKey.PROJECT_MANAGER.toString(),
                    },
                    {
                      value: RoleKey[RoleKey.CUSTOMER_SUPPORT],
                      label: RoleKey.CUSTOMER_SUPPORT.toString(),
                    },
                  ]}
                />
                <SelectFieldComponent
                  defaultValue={assignationTransfer ? BillingPolicy[assignationTransfer.billingPolicy!] : ""}
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"billingPolicy"}
                  label="Billing Policy"
                  required={true}
                  readOnly={!canChangePositionNature && !positionCreation}
                  options={[
                    {
                      value: BillingPolicy[BillingPolicy.SUM_HOURS],
                      label: BillingPolicy.SUM_HOURS.toString(),
                    },
                    {
                      value: BillingPolicy[BillingPolicy.SUM_HOURS_WITH_MINIMUM],
                      label: BillingPolicy.SUM_HOURS_WITH_MINIMUM.toString(),
                    },
                  ]}
                  internalSelectProps={{
                    onChange: (event) => handleOnChangeInputBillingPolicy(event),
                  }}
                />
                {minimumHoursPolicy && (
                  <NumberField
                    control={control}
                    register={register}
                    errors={errors}
                    propertyName={"monthlyMinimumHours"}
                    label="Monthly Minimum Hours"
                    defaultValue="176"
                    required={false}
                    pattern={new RegExp("^[1-9]+[0-9]*$")}
                    customHelperText={"Monthly minimum hours should be greater than zero"}
                    readOnly={(!canChangePositionNature && !positionCreation) || !minimumHoursPolicy}
                    min={0}
                    enableEditInputProps
                  />
                )}

                <DateFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"positionShortStartDate"}
                  label="Start Date"
                  required={true}
                  clearable={true}
                  readOnly={!canChangePositionNature && !positionCreation}
                  toUTC
                />

                <DateFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"positionShortEndDate"}
                  label="End Date"
                  required={false}
                  clearable={true}
                  toUTC
                />

                <NumberField
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"bonus"}
                  label="Bonus %"
                  required={false}
                  max={100}
                  min={0}
                  defaultValue={"0"}
                  pattern={new RegExp("^[0-9]$|^[1-9][0-9]$|^(100)$")}
                  customHelperText={"Bonus should be between 0 and 100"}
                  enableEditInputProps
                  readOnly={!canChangePositionNature && !positionCreation}
                />

                <Box sx={{ display: "flex", gap: "20px" }}>
                  <CheckboxComponent
                    control={control}
                    register={register}
                    errors={errors}
                    propertyName={"shadow"}
                    label="Shadow Position"
                    required={false}
                    readOnly={!canChangePositionNature && !positionCreation}
                  />
                  <CheckboxComponent
                    control={control}
                    register={register}
                    errors={errors}
                    propertyName={"onDemand"}
                    label="On Demand"
                    required={false}
                    readOnly={!canChangePositionNature && !positionCreation}
                  />
                </Box>
              </>
            )}
          </SpinnerWrapper>
        </Box>
      )}
    />
  );
}

const Input = styled("input")({
  display: "none",
});
